/**
 * Currency picker for the redesigned wizard steps.
 *
 * Two render modes, and deliberately two DIFFERENT elements:
 *
 * - Changeable → a real Select.
 * - Locked → static text plus an info tooltip, NOT a disabled Select. scio1 rendered a
 *   `ng-disabled` <select> here (subscriptionPlanModal.html:105) and we intentionally do not
 *   mirror that: a greyed dropdown with a chevron advertises an affordance it does not have,
 *   invites a click that silently does nothing, and at 50% opacity over muted grey it fails
 *   contrast as well. The label itself still earns its place, because "$" alone is ambiguous
 *   between USD, CAD and AUD — a Canadian customer needs to see which dollar the prices are
 *   quoted in. That is a job for text, not for a control.
 *
 * Lives with the content rather than in the page header so it right-aligns to the card grid's
 * edge (the header's own right column is the full page width, which put it out at the viewport
 * margin instead). Extracted because both redesigned steps render it and duplicating the
 * five options in two places is how they drift apart.
 */
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@scio3/ui';
import { Info } from 'lucide-react';

/** One source for both the dropdown options and the locked label, so the two cannot drift. */
const CURRENCY_OPTIONS = [
  { value: 'usd', label: 'USD $' },
  { value: 'eur', label: 'EUR €' },
  { value: 'gbp', label: 'GBP £' },
  { value: 'cad', label: 'CAD $' },
  { value: 'aud', label: 'AUD $' },
] as const;

type Props = {
  value: string;
  onChange: (currency: string) => void;
  /**
   * A subscription already fixes the currency → render it read-only instead of as a control
   * nobody can operate. See `canChangeCurrency` in useSubscriptionWizard for how this is derived.
   */
  locked?: boolean;
  className?: string;
};

export function CurrencySelect({ value, onChange, locked, className }: Props) {
  const { t } = useTranslation();

  if (locked) {
    // Unknown codes fall back to the raw code rather than rendering blank.
    const label =
      CURRENCY_OPTIONS.find((option) => option.value === value)?.label ?? value.toUpperCase();

    return (
      <div
        className={cn('flex items-center gap-1.5 text-sm', className)}
        data-testid="wizard-currency-locked"
      >
        <span className="font-medium text-foreground">{label}</span>
        {/* Own provider: there is no global TooltipProvider in this app — every consumer
            wraps its own, and Radix's Tooltip.Root throws without one. */}
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              {/* A real button, not the <span>: the explanation has to be reachable by
                  keyboard, and Radix opens the tooltip on focus. */}
              <button
                type="button"
                className="cursor-help"
                aria-label={t('Why is my currency locked?')}
              >
                <Info className="h-4 w-4 text-muted-foreground" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              {t(
                'Your billing currency is set by your subscription and cannot be changed. To change it, please contact support.',
              )}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        className={cn('w-[120px] cursor-pointer text-sm', className)}
        aria-label={t('Currency')}
        data-testid="wizard-currency-select"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {CURRENCY_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={option.value} className="cursor-pointer">
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
